<script lang="ts">
  // BJL-CHECK: Dropdown, Menu, Checkbox, ToggleGroup — the rows are checkbox-shaped but
  // each is a single action that re-runs the search on tap (no form, no submit), and one
  // row is deliberately *locked* rather than merely unchecked (unchecking the last mode
  // would leave nothing to ride) — a state bjl's disabled Checkbox reads as "unavailable"
  // rather than "already required". See artifacts/deviations/PlanPage.md.
  //
  // Settings button → checkbox dropdown: which modes to ride, express buses or not.
  // Every setter re-searches on its own (see the store), so there's no Apply button.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Colors } from '../../ui/colors'
  import { Type } from '../../ui/theme'
  import { useTripPlanStore } from '../../store/tripPlan'
  import { storeSelect } from '../../lib/store.svelte'
  import { optionsSummary, toggleMode } from './format'

  const modes = storeSelect(useTripPlanStore, (s) => s.modes)
  const expressBus = storeSelect(useTripPlanStore, (s) => s.expressBus)

  let open = $state(false)

  const subwayOn = $derived(modes.value.includes('subway'))
  const busOn = $derived(modes.value.includes('bus'))
</script>

{#snippet optionRow(
  label: string,
  detail: string | undefined,
  checked: boolean,
  disabled: boolean,
  onToggle: () => void,
)}
  <button
    onclick={onToggle}
    {disabled}
    aria-pressed={checked}
    style="
      display: flex; align-items: center; gap: 10px; width: 100%;
      padding: 10px 12px; border: none; background-color: transparent;
      text-align: left; cursor: {disabled ? 'default' : 'pointer'};
      opacity: {disabled ? 0.45 : 1};
    "
  >
    <span
      style="
        width: 18px; height: 18px; border-radius: 5px; flex-shrink: 0;
        border: 1px solid {checked ? Colors.primary : Colors.borderStrong};
        background-color: {checked ? Colors.primary : 'transparent'};
        color: #fff; font-size: 12px; font-weight: 900;
        display: flex; align-items: center; justify-content: center;
      "
    >{checked ? '✓' : ''}</span>
    <span style="display: flex; flex-direction: column; min-width: 0;">
      <span style="color: {Colors.text}; {Type.bodyLg}; font-weight: 700;">{label}</span>
      {#if detail}
        <span style="color: {Colors.textMuted}; {Type.caption};">{detail}</span>
      {/if}
    </span>
  </button>
{/snippet}

<div style="position: relative; width: 100%;">
  <Button
    variant={open ? 'default' : 'secondary'}
    size="sm"
    onclick={() => { open = !open }}
    aria-expanded={open}
    style="width: 100%; position: relative; z-index: 45;"
  >
    {optionsSummary(modes.value, expressBus.value)} {open ? '▴' : '▾'}
  </Button>

  {#if open}
    <!-- Full-screen catcher: a tap anywhere else closes the dropdown. -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div onpointerdown={() => { open = false }} style="position: fixed; inset: 0; z-index: 40;"></div>
    <div
      style="
        position: absolute; top: 100%; left: 0; margin-top: 6px; min-width: 240px;
        background-color: {Colors.surfaceElevated};
        border: 1px solid {Colors.borderStrong}; border-radius: var(--bjl-radius-md, 10px);
        box-shadow: var(--bjl-shadow-lg, 0 8px 24px rgba(0,0,0,0.55));
        z-index: 50; overflow: hidden;
      "
    >
      <div
        style="padding: 8px 12px 4px; color: {Colors.textMuted}; {Type.eyebrow};"
      >RIDE</div>
      <!-- Unchecking the last mode would leave nothing to ride; blocked here so the row
           reads as locked rather than silently ignoring the tap. -->
      {@render optionRow('Subway', undefined, subwayOn, subwayOn && !busOn, () =>
        useTripPlanStore.getState().setModes(toggleMode(modes.value, 'subway')),
      )}
      {@render optionRow('Bus', undefined, busOn, busOn && !subwayOn, () =>
        useTripPlanStore.getState().setModes(toggleMode(modes.value, 'bus')),
      )}
      <div style="height: 1px; background-color: {Colors.border};"></div>
      {@render optionRow(
        'MTA express buses',
        'Higher fare · commuter routes',
        busOn && expressBus.value,
        !busOn,
        () => useTripPlanStore.getState().setExpressBus(!expressBus.value),
      )}
    </div>
  {/if}
</div>
